import { Sheet, SheetContent } from "@/v2-components/ui/sheet";
import type { CartTrigger } from "@/libs/analytics/cart-trigger";
import { useMedia } from "react-use";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { Product, ProductSlug } from "@/api/product-api";
import { useState } from "react";
import { useTrans } from "@/utils/useTrans";
import { Badge } from "@/v2-components/ui/badge";
import { GePrice } from "@/v2-components/GePrice";
import { cn } from "@/libs/utils";
import { useLocale } from "@/store/locale-store";
import { useProductPrice } from "@/hooks/useProductPrice";
import { Button } from "@/v2-components/ui/button";
import {
  SheetClose,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import { CartItemOption } from "@/store/cart-store";
import { SelectProductOption } from "@/v2-components/cart-sheet/SelectProductOption";
import { ProductUtil } from "@/utils/ProductUtil";

export function SelectRecommendedBundle({
  products,
  onContinue,
  onClose,
  trigger = "unknown",
  notifyOnAdd = true,
}: {
  products: Product[];
  onContinue: (item: {
    slug: ProductSlug;
    quantity: number;
    options: CartItemOption[];
    sku: string;
  }) => void;
  onClose: () => void;
  /**
   * `add_to_cart` 의 진입점 (#5292). **그대로 아래 시트로 흘린다.**
   *
   * ⚠️ 이 시트의 Continue 는 SKU 개수로 갈린다 — 옵션이 여럿이면 `SelectProductOption`
   * 을 한 번 더 띄운다. 거기까지 안 넘기면 그 경로만 `unknown` 으로 나가고,
   * **정상 동작에서 `unknown` 이 쌓이면 계측 공백을 알려주는 신호가 죽는다.**
   */
  trigger?: CartTrigger;
  /**
   * 담기 완료 팝(#5366)을 띄울 표면인가 — **그대로 아래 시트로 흘린다**(`trigger` 와 같다).
   *
   * ⚠️ 여기서 판정하지 않는다. 이 컴포넌트의 `onContinue` 갈래는 **호출처(`useAddItemToCart`)
   * 가 이미 판정**하므로, 여기서 또 보면 같은 동작이 두 경로로 갈린다. 이 prop 은 **내부
   * `SelectProductOption` 에 전달하는 용도만** 이다.
   *
   * 이게 없어서 이음매가 끊겼다(#5366 리뷰 R2, `introduced-by-fix`):
   * 카트 레일(`notifyOnAdd={false}`) → 추천 번들 시트 → 내부 옵션 시트가 기본값 `true` 로
   * 가서 **카트 화면에서 팝이 떴다.**
   */
  notifyOnAdd?: boolean;
}) {
  const isMobile = useMedia("(max-width: 1023px)", false);
  const { t } = useTrans(["gnb"]);
  const [selectedProduct, setSelectedProduct] = useState<Product>(products[0]);
  const [showSkuOptionsDialog, setShowSkuOptionsDialog] = useState(false);

  return (
    <>
      {showSkuOptionsDialog && selectedProduct && (
        <SelectProductOption
          trigger={trigger}
          notifyOnAdd={notifyOnAdd}
          product={selectedProduct}
          onClose={() => {
            setShowSkuOptionsDialog(false);
            onClose();
          }}
        />
      )}
      <Sheet
        open={true}
        onOpenChange={() => {
          onClose();
        }}
      >
        <SheetContent
          className="flex flex-col laptop:min-w-[634px]"
          side={isMobile ? "bottom" : "right"}
        >
          <SheetHeader className="items-center justify-between">
            <SheetTitle>{t(`Recommended Sets`)}</SheetTitle>
            <SheetDescription className="sr-only">
              Select the recommended set to add to your bag.
            </SheetDescription>
            <SheetClose className="focus:outline-none" onClick={onClose}>
              <CrossIcon />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>

          <ul className="-mx-4 overflow-y-auto px-4 pb-20">
            {products.map((_product) => (
              <Item
                key={_product.slug}
                currentProduct={_product}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
              />
            ))}
          </ul>

          <section className="absolute bottom-0 -mx-4 flex w-full flex-col gap-2 border-t border-coolgray-20 bg-white p-4 pb-5 laptop:-mx-8 laptop:p-8">
            <Button
              size="sm"
              variant="blue-fill"
              shape="pill"
              className="flex px-4 py-3 laptop:px-5 laptop:py-4 laptop:text-lg"
              onClick={() => {
                if (selectedProduct.skuOptions.length > 1) {
                  setShowSkuOptionsDialog(true);
                  return;
                }
                onContinue({
                  slug: selectedProduct.slug,
                  quantity: 1,
                  options: ProductUtil.getOptionsFromSkuOption(
                    selectedProduct.skuOptions[0],
                  ),
                  sku: selectedProduct.skuOptions[0].sku,
                });
              }}
              disabled={!selectedProduct}
            >
              Continue
            </Button>
          </section>
        </SheetContent>
      </Sheet>
    </>
  );
}

interface ItemProps {
  currentProduct: Product;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product) => void;
}
function Item({
  currentProduct,
  selectedProduct,
  setSelectedProduct,
}: ItemProps) {
  const { countryCode } = useLocale();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(currentProduct);
  const { displayOriginalPrice, displayDiscountAmount, displaySalePrice } =
    useProductPrice(price);

  if (!currentProduct || !price) {
    return null;
  }

  return (
    <li
      className={cn(
        "mt-3 flex select-none flex-col justify-between gap-2 rounded-lg border border-coolgray-20 p-5 transition-colors hover:border-coolgray-40 laptop:flex-row laptop:items-center",
        selectedProduct?.slug === currentProduct.slug &&
          "outline outline-2 -outline-offset-2 outline-blue-60",
      )}
      onClick={() => setSelectedProduct(currentProduct)}
    >
      <div className="flex flex-col gap-1">
        <span className="text-lg font-600">{currentProduct.name}</span>

        {currentProduct.slug === ProductSlug.FPS_BUNDLE_TACTVISOR && (
          <span>{`(TactSuit Pro + TactSleeve + TactVisor)`}</span>
        )}
        {currentProduct.slug === ProductSlug.FPS_BUNDLE && (
          <span>{`(TactSuit Pro + TactSleeve)`}</span>
        )}
        {currentProduct.slug === ProductSlug.STARTER_BUNDLE && (
          <span>{`(TactSuit Air + TactSleeve)`}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1 self-end laptop:self-auto">
        {displayDiscountAmount > 0 && (
          <Badge variant="sale" size="sm" className="mr-1 gap-x-1">
            Save
            <GePrice
              amount={displayDiscountAmount}
              currency={price.currency}
              countryCode={countryCode}
            />
          </Badge>
        )}
        <GePrice
          className="font-600"
          amount={displaySalePrice}
          currency={price.currency}
          countryCode={countryCode}
        />

        {!!displayDiscountAmount && (
          <GePrice
            className="text-coolgray-50 line-through"
            amount={displayOriginalPrice}
            currency={price.currency}
            countryCode={countryCode}
          />
        )}
      </div>
    </li>
  );
}
